<template>
    <div class="tw:w-full">
        <ValidationProvider ref="provider" :name="props.name" :rules="computedRules" v-slot="{ errors }" mode="eager">
            <div class="tw:relative tw:w-full">
                <!-- Icon slot on the left -->
                <div
                    v-if="icon"
                    class="tw:absolute tw:left-3 tw:top-1/2 tw:-translate-y-1/2 tw:text-slate-400 tw:pointer-events-none"
                >
                    <slot name="icon">
                        <svg class="tw:w-5 tw:h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" v-html="icon" />
                    </slot>
                </div>

                <input
                    v-model="localValue"
                    :id="inputId"
                    :name="props.name"
                    :aria-invalid="errors[0] ? 'true' : 'false'"
                    :aria-describedby="errors[0] ? errorId : undefined"
                    :type="props.type"
                    :placeholder="' '"
                    :maxlength="props.maxlength"
                    :disabled="props.disabled"
                    class="tw:block tw:px-2 tw:py-2 tw:w-full tw:text-sm tw:rounded-md tw:border tw:appearance-none tw:focus:outline-none tw:peer"
                    :class="[
                        errors[0]
                            ? 'tw:border-red-500 tw:bg-red-50/50 tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400 tw:focus:bg-white'
                            : 'tw:border-slate-200 tw:bg-white tw:focus:border-blue-400 tw:focus:caret-blue-400 tw:focus:text-blue-400',
                        props.maxlength ? 'tw:pr-10' : '',
                        icon ? 'tw:pl-10' : ''
                    ]"
                    v-bind="$attrs"
                    @input="handleInput"
                    @change="handleChange"
                />
                <label
                    v-if="props.label"
                    :for="inputId"
                    class="tw:absolute tw:uppercase tw:text-xs tw:md:text-sm tw:duration-300 tw:z-10 tw:pointer-events-none tw:tracking-wide tw:origin-left tw:bg-white tw:px-2
                    tw:start-1 tw:top-2 tw:-translate-y-4
                    tw:peer-placeholder-shown:scale-100 tw:peer-placeholder-shown:-translate-y-1/2 tw:peer-placeholder-shown:top-1/2 tw:peer-placeholder-shown:font-normal
                    tw:peer-focus:top-2 tw:peer-focus:-translate-y-4 tw:peer-focus:scale-75 tw:peer-focus:px-2 tw:peer-focus:font-bold tw:peer-focus:text-blue-800"
                    :class="[
                        errors[0]
                            ? 'tw:text-red-400 tw:peer-placeholder-shown:bg-transparent tw:peer-focus:bg-white tw:peer-focus:text-blue-800'
                            : 'tw:text-slate-400 tw:font-bold',
                        icon ? 'tw:start-10' : ''
                    ]"
                >
                    {{ props.label }}
                </label>
                <span
                    v-if="props.maxlength"
                    class="tw:absolute tw:right-2 tw:top-1/2 tw:-translate-y-1/2 tw:text-xs tw:text-slate-400"
                >
                    {{ localValue.length }}/{{ props.maxlength }}
                </span>
            </div>

            <span
                v-if="errors[0]"
                :id="errorId"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ formatErrorMessage(errors[0]) }}
            </span>

            <span
                v-if="props.apiValidationError && !errors[0]"
                class="tw:block tw:mt-1 tw:text-xs tw:text-red-600 tw:font-medium"
            >
                {{ Array.isArray(props.apiValidationError) ? props.apiValidationError.join(', ') : props.apiValidationError }}
            </span>
        </ValidationProvider>
    </div>
</template>

<script setup>
    import { ref, computed, watch, nextTick } from 'vue';

    const provider = ref(null);

    const props = defineProps({
        type: { type: String, default: 'text' },
        label: String,
        id: String,
        name: String,
        rules: [String, Object],
        value: [String, Number],
        disabled: Boolean,
        maxlength: [String, Number],
        apiValidationError: [String, Array],
        icon: String,
    });

    const emit = defineEmits(['input', 'change']);

    const computedRules = computed(() => props.rules || '');
    const inputId = computed(() => props.id || (props.name ? `input-${props.name.replace(/[^a-zA-Z0-9_-]/g, '-')}` : undefined));
    const errorId = computed(() => inputId.value ? `${inputId.value}-error` : undefined);

    const localValue = ref(props.value != null ? String(props.value) : '');

    const formatErrorMessage = err => {
        let msg = typeof err === 'string' ? err : (err && err.message) || String(err || '');
        // Replace field name with label if available for better UX
        if (props.label && props.name && msg.includes(props.name)) {
            msg = msg.replace(props.name, props.label);
        }
        return msg ? msg.charAt(0).toUpperCase() + msg.slice(1).toLowerCase() : '';
    };

    const handleInput = e => {
        const value = e.target.value;
        localValue.value = value;
        emit('input', value);
    };

    const handleChange = e => {
        const value = e.target.value;
        localValue.value = value;
        emit('input', value);
        emit('change', value);
    };

    watch(() => props.value, newVal => {
        localValue.value = newVal != null ? String(newVal) : '';
        nextTick(() => provider.value?.validate());
    });
</script>

<script>
    export default {
        inheritAttrs: false
    };
</script>
